import React from 'react';
import styled from 'styled-components';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import Card from '@material-ui/core/Card';
import CardMedia from '@material-ui/core/CardMedia';
import Generic from '../../../components/molecule/SortableElements/Generic';
import FloatingSelect from '../../../components/molecule/FloatingSelect';
import Button from '../../../components/atomic/Buttons/Button';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  width: 100%;
`;

const SetWrapper = styled.div`
  border: 1px solid #ccc;
  background: #eee;
  border-radius: 10px;
  display: flex;
  padding: 10px;
  margin-bottom: 25px;
  flex-direction: column;
  width: 100%;
`;

const SetHeader = styled.div`
  color: #999;
  font-size: 22px;
  font-weight: 400;
  margin-bottom: 10px;
  text-align: center;
  width: 100%;
`;

const CardStyled = styled(Card)`
  display: flex;
  min-height: 75px;
  min-width: 400px;
  margin: 10px 0;
`;
const CardMediaStyled = styled(CardMedia)`
  min-width: 75px;
  min-height: 75px;
`;
const Details = styled.div`
  display: flex;
  overflow: hidden;
  position: relative;
  flex: 1 1 auto;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  max-width: 100%;
`;

const ActivityWrapper = styled.div`
  border: 1px solid #ccc;
  background: white;
  border-radius: 10px;
  display: flex;
  padding: 10px;
  margin-bottom: 25px;
  flex-direction: column;
  width: 100%;
`;

const DaysWrapper = styled.div`
  border: 1px solid #ccc;
  background: white;
  border-radius: 10px;
  display: flex;
  padding: 10px;
  margin-bottom: 25px;
  flex-direction: column;
  width: 100%;
`;

const ProgramSets = ({ sets, onActivityRemove, onWorkoutRemove, onWorkoutSortEnd, onActivitySortEnd, addWorkout, addActivity, addDay }) => {
  return (
    <Container>
      {sets.map((programSet, index) => (
        <ProgramSet
          key={`program-set-${index}`}
          programSet={programSet}
          setIndex={index}
          onActivityRemove={onActivityRemove}
          onWorkoutRemove={onWorkoutRemove}
          onWorkoutSortEnd={onWorkoutSortEnd}
          onActivitySortEnd={onActivitySortEnd}
          addWorkout={addWorkout}
          addActivity={addActivity}
          addDay={addDay}
        />
      ))}
    </Container>
  );
}

const SortableClass = SortableElement(({ item, classIndex, setIndex, dayIndex, onRemove }) => {
  return (
  <Generic index={classIndex} onClick={() => onRemove(setIndex, dayIndex, classIndex)}>
    <CardStyled>
      {
        item.image && item.image.url && <CardMediaStyled image={item.image.url} />
      }
      <Details>{item.title}</Details>
    </CardStyled>
  </Generic>
  );
});

const SortableSetContainer = SortableContainer(({ classes, setIndex, dayIndex, onRemove }) => {
  return (
    <div>
      {classes.map((item, index) => (
        <SortableClass
          key={`sortable-class-${setIndex}-${dayIndex}-${index}`}
          index={index}
          item={item}
          classIndex={index}
          setIndex={setIndex}
          dayIndex={dayIndex}
          onRemove={onRemove}
        />
      ))}
    </div>
  );
});

const SortableActivity = SortableElement(({ item, activityIndex, setIndex, onRemove }) => {
  return (
    <Generic index={activityIndex} onClick={() => onRemove(setIndex, activityIndex)}>
      <CardStyled>
        {
          item.image && item.image.url && <CardMediaStyled image={item.image.url} />
        }
        <Details>{item.name}</Details>
      </CardStyled>
    </Generic>
  );
});

const SortableActivityContainer = SortableContainer(({ activities, setIndex, onRemove }) => {
  return (
    <div>
      {activities.map((item, index) => (
        <SortableActivity
          key={`sortable-activity-${setIndex}-${index}`}
          index={index}
          item={item}
          activityIndex={index}
          setIndex={setIndex}
          onRemove={onRemove}
        />
      ))}
    </div>
  );
});

const DayContainer = ({ days, setIndex, addWorkout, addDay, onWorkoutRemove, onWorkoutSortEnd }) => {
  const daysListed = days.filter(item => item.label);
  if (daysListed.length === 0) {
    days[0] = {
      label: 'Day 1',
      classes: [],
    };
  }
  return (
    <div>
      {days.map((item, dayIndex) => (
        item.label
          ? (
            <div key={`day-container-${setIndex}-${dayIndex}`}>
              <SetHeader>{item.label}</SetHeader>
              <SortableSetContainer
                classes={item.classes}
                setIndex={setIndex}
                dayIndex={dayIndex}
                onRemove={onWorkoutRemove}
                onSortEnd={(o) => onWorkoutSortEnd(o, setIndex, dayIndex)}
                useDragHandle
              />
              <FloatingSelect>
                <Button color="primary" variant="contained" onClick={() => addWorkout(setIndex, dayIndex)}>
                  Add Workout to {item.label}
                </Button>
              </FloatingSelect>
            </div>
          ) : null
      ))}
      {daysListed.length < 7 && (
        <FloatingSelect>
          <Button color="primary" variant="contained" disabled={ daysListed.length === 0 || !days[daysListed.length - 1].classes || !days[daysListed.length - 1].classes.length } onClick={() => addDay(setIndex)}>
            Add Day
          </Button>
        </FloatingSelect>
      )}
    </div>
  );
}

// a ProgramSet is a week
const ProgramSet = ({ programSet, setIndex, onActivityRemove, onWorkoutRemove, onWorkoutSortEnd, onActivitySortEnd, addWorkout, addActivity, addDay }) => {
  return (
    <SetWrapper>
      <SetHeader>{programSet.name}</SetHeader>

      <ActivityWrapper>
        <SetHeader>Wellness Activities</SetHeader>
        <SortableActivityContainer
          activities={programSet.activities.wellness}
          setIndex={setIndex}
          onRemove={onActivityRemove}
          onSortEnd={(o) => onActivitySortEnd(o, setIndex)}
        />
        <FloatingSelect>
          <Button color="primary" variant="contained" onClick={() => addActivity(setIndex)}>
            Add Activity
          </Button>
        </FloatingSelect>
      </ActivityWrapper>

      <DaysWrapper>
        <SetHeader>Physical Activities</SetHeader>
        <DayContainer
          days={programSet.activities.physical}
          setIndex={setIndex}
          addWorkout={addWorkout}
          addDay={addDay}
          onWorkoutRemove={onWorkoutRemove}
          onWorkoutSortEnd={onWorkoutSortEnd}
        />
      </DaysWrapper>
    </SetWrapper>
  )
}

export default ProgramSets;
